<template>
  <div class="issued-pass-list">
    <vue-headful title="Реестр выданных пропусков" />

    <div class="container">
      <div class="content">
        <alert v-if="xlsStatus" dismissible :type="alertType" @dismissed="xlsStatus = ''">{{ xlsStatus }}</alert>
        <div class="row">
          <div class="col-md-8">
            <h2>Реестр выданных пропусков</h2>
          </div>
        </div>

        <hr>

        <div class="row filters-block">
          <div class="col-sm-6 first-filter-input-block">
            <div class="input-group filter-date-range">
              <date-picker
                v-model="dateRange"
                :disabled-date="disabledDates"
                :range="true"
                format="DD.MM.YYYY"
                value-type="YYYY-MM-DD"
                placeholder="Период выдачи"
                @change="handleSearch()"
              />
              <div class="input-group-btn">
                <button type="button" class="btn btn-default btn-left" @click="onClickExportExcel">
                <span>
                  <img :src="baseUrl + '/vue/img/excel_icon.png'" alt='export-excel' style="height: 17px">
                </span>
                </button>
              </div>
              <div v-if="visibleExportPdfButton" class="input-group-btn">
                <button type="button" class="btn btn-default" @click="onClickExportPdf">
                <span>
                  <img :src="baseUrl + '/vue/img/pdf_file.svg'" alt='export-pdf' style="height: 17px">
                </span>
                </button>
              </div>
            </div>
          </div>

          <div class="col-sm-6 filter-input-block">
            <select
              v-model="transportTypeFilter"
              id="transport-type-filter"
              class="show-tick form-control"
              @change="changeTransportTypeFilter($event)"
            >
              <option value="all">Все типы ТС</option>
              <option
                v-for="(type, key) in transportTypes"
                :key="key"
                :value="key"
              >{{ type }}
              </option>
            </select>
          </div>
        </div>

        <div class="row filters-block">
          <div class="col-sm-6 first-filter-input-block">
            <input
              v-model="organizationSearch"
              type="text"
              class="form-control"
              placeholder="Поиск по организации"
              @change="changeOrganizationSearch($event)"
            >
          </div>

          <div class="col-sm-6 filter-input-block">
            <input
              v-model="vesselSearch"
              type="text"
              class="form-control"
              placeholder="Поиск по судну"
              @change="changeVesselSearch($event)"
            >
          </div>
        </div>

        <div class="row filters-block">
          <div class="col-sm-6 first-filter-input-block">
            <input
              v-model="cardCodeSearch"
              type="text"
              class="form-control"
              placeholder="Поиск по коду карты"
              @change="changeCardCodeSearch($event)"
            >
          </div>
        </div>

        <div v-if="issuedPasses && issuedPasses.length">
          <div class="row">
            <div class="col-md-12">
              <div v-for="issuedPass in issuedPasses" :key="issuedPass.id">
                <issued-pass-item :issued-pass="issuedPass" />
              </div>
            </div>
          </div>
        </div>

        <infinite-loading :identifier="infiniteId" @infinite="infiniteLoadIssuedPasses">
          <div slot="no-more">Показаны все записи</div>
          <div slot="no-results">Нет записей</div>
        </infinite-loading>
      </div>
    </div>
  </div>
</template>

<script>
import '@/common/assets/styles/issued-pass-list.scss'
import mainMixin from '@/common/mixins/mainMixin'
import { ApiClient } from '@/common/helpers/api-client/ApiClient'
import axios from 'axios'
import moment from 'moment/moment'
import PermissionsMixin from '@/common/mixins/PermissionsMixin'
import PermissionsEnum from '@/common/enums/PermissionsEnum'
import IssuedPassItem from '@/common/components/IssuedPass/IssuedPassItem.vue'

export default {
  name: 'IssuedPassList',

  components: { IssuedPassItem },

  mixins: [mainMixin, PermissionsMixin],

  data() {
    return {
      alertType: '',
      baseUrl: process.env.VUE_APP_API_BASE_URL,
      cardCodeSearch: '',
      dateRange: [],
      infiniteId: 0,
      issuedPasses: [],
      organizationSearch: '',
      vesselSearch: '',
      pagination: {
        active: 1,
        per: 10
      },
      preloader: true,
      searchChanged: false,
      transportTypeFilter: 'all',
      transportTypes: [],
      xlsStatus: ''
    }
  },

  computed: {
    filterParams() {
      return {
        dateRange: this.dateRange,
        transportTypeFilter: this.transportTypeFilter,
        organizationSearch: this.organizationSearch,
        vesselSearch: this.vesselSearch,
        cardCodeSearch: this.cardCodeSearch,
        page: this.pagination.active
      }
    },

    visibleExportPdfButton() {
      return this.hasPermission(PermissionsEnum.canUsePdfExportOnIssuedPassesPage)
    }
  },

  async created() {
    if (!this.hasPermission(PermissionsEnum.canUseIssuedPassesPage)) {
      window.location.href = ApiClient.getApplicationScreenUrl()
    }

    const vue = this
    this.pagination.active = history.state?.page ?? 1
    await this.fetchData()

    this.hidePreloader()
    window.addEventListener('popstate', (event) => {
      if (event.state) {
        vue.fillSearchParamsFromState(event)
        vue.clearSearch(this.pagination.active)
      }
    })
  },

  methods: {
    changeCardCodeSearch(event) {
      if (this.searchChanged) {
        return
      }

      this.searchChanged = true
      this.cardCodeSearch = event.target.value

      this.changeSearch()
    },

    changeOrganizationSearch(event) {
      if (this.searchChanged) {
        return
      }

      this.searchChanged = true
      this.organizationSearch = event.target.value

      this.changeSearch()
    },

    changeSearch() {
      this.insertSearchParams()
      this.clearSearch(this.pagination.active)

      setTimeout(() => {
        this.searchChanged = false
      }, 500)
    },

    changeTransportTypeFilter(event) {
      if (this.searchChanged) {
        return
      }

      this.searchChanged = true
      this.transportTypeFilter = event.target.value

      this.changeSearch()
    },

    changeVesselSearch(event) {
      if (this.searchChanged) {
        return
      }

      this.searchChanged = true
      this.vesselSearch = event.target.value

      this.changeSearch()
    },

    async clearSearch(page) {
      this.pagination.active = page
      this.issuedPasses = []
      this.infiniteId += 1
    },

    disabledDates(date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return date > today;
    },

    async fetchData() {
      const data = await ApiClient.extraData().then(response => response?.data?.data)
      this.setExtraData(data)
    },

    fillSearchParamsFromState({
      dateRange,
      transportTypeFilter,
      organizationSearch,
      vesselSearch,
      cardCodeSearch,
      page
    }) {
      this.dateRange = dateRange ?? []
      this.transportTypeFilter = transportTypeFilter ?? 'all'
      this.organizationSearch = organizationSearch ?? ''
      this.vesselSearch = vesselSearch ?? ''
      this.cardCodeSearch = cardCodeSearch ?? ''
      this.pagination.active = page ?? 1
    },

    async getPageIssuedPasses(activePage, perPage = null) {
      let results = []

      await ApiClient.getIssuedPassList({
        page: activePage,
        perPage: perPage || this.pagination.per,
        dateOf: this.dateRange[0],
        dateTo: this.dateRange[1],
        transportType: this.transportTypeFilter,
        organization: this.organizationSearch,
        vessel: this.vesselSearch,
        cardCode: this.cardCodeSearch
      }).then((response) => {
        results = response?.data?.data?.models
      })

      return results
    },

    handleSearch() {
      if (this.searchChanged) {
        return
      }
      this.searchChanged = true
      this.changeSearch()
    },

    async infiniteLoadIssuedPasses($state) {
      let issuedPasses
      if (!this.issuedPasses.length) {
        this.pagination.active = 1
        if (Object.keys(this.$route.query).length) {
          this.fillSearchParamsFromState({
            dateRange: this.dateRange ?? this.$route.query.dateRange,
            transportTypeFilter: this.transportTypeFilter ?? this.$route.query.transportTypeFilter,
            organizationSearch: this.organizationSearch ?? this.$route.query.organizationSearch,
            vesselSearch: this.vesselSearch ?? this.$route.query.vesselSearch,
            cardCodeSearch: this.cardCodeSearch ?? this.$route.query.cardCodeSearch,
            page: this.pagination.active ?? this.$route.query.page ?? 1
          })
        }
      }

      issuedPasses = await this.getPageIssuedPasses(this.pagination.active)

      if (issuedPasses?.length) {
        this.pagination.active++
        this.issuedPasses.push(...issuedPasses)
        $state.loaded()
      } else if (issuedPasses?.length === 0) {
        $state.complete()
      } else {
        $state.error()
      }

      this.replaceStatePageData()
    },

    async insertSearchParams() {
      this.pagination.active = 1
      await this.$router.push({ query: { ...this.filterParams } })
    },

    onClickExportExcel() {
      const vue = this;

      vue.xlsStatus = 'Экспорт данных начался.'
      vue.alertType = 'info'
      axios({
        method: 'get',
        url: this.baseUrl + '/export/issued' +
          '?dateOf=' + (this.dateRange[0] ?? '') + '&dateTo=' + (this.dateRange[1] ?? '') +
          '&transportType=' + this.transportTypeFilter + '&organization=' + this.organizationSearch +
          '&vessel=' + this.vesselSearch + '&cardCode=' + this.cardCodeSearch,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
          Accept: 'application/json, text/javascript, */*; q=0.01'
        },
        responseType: 'blob'
      }).then(function (result) {
        if (Number(result.status) !== 400) {
          const url = window.URL.createObjectURL(new Blob([result.data]))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute(
            'download', 'export_' + moment().format('DD.MM.YYYY_hh:mm:ss') + '.xlsx'
          )
          link.click()
          link.remove()
          vue.xlsStatus = 'Данные успешно экспортированы'
          vue.alertType = 'success'
        } else {
          vue.xlsStatus = result.message ? result.message : 'Не удалось экспортировать данные.'
          vue.alertType = 'danger'
        }
      })
        .catch(function () {
          vue.xlsStatus = 'Не удалось экспортировать данные.'
          vue.alertType = 'danger'
        });
    },

    async onClickExportPdf() {
      const pdfPassBlobResponse = await ApiClient.getIssuedPasses(
        this.dateRange[0] ?? '',
        this.dateRange[1] ?? '',
        this.transportTypeFilter,
        this.organizationSearch,
        this.vesselSearch,
        this.cardCodeSearch
      )
      window.open(URL.createObjectURL(pdfPassBlobResponse.data), '_blank')
    },

    replaceStatePageData() {
      const params = history.state ?? this.filterParams
      params.page = this.pagination.active

      const url = new URL(window.location)
      url.searchParams.set('page', this.pagination.active)
      history.replaceState(params, undefined, url.href)
    },

    setExtraData(data) {
      this.transportTypes = data?.transportTypes
    }
  }
}
</script>
